/**
 * Banner styles. Inlined into the shadow root at runtime.
 * Naming convention: every CSS class and every CSS custom property is
 * prefixed with `csteer_` (underscore) — matches the legacy convention
 * documented in legacy/app/login.css. EVERY visual property goes through
 * a CSS variable so themes can be swapped by overriding a small set
 * of variables on :host.
 */

:host {
  /* === Defaults (overridable by server via JS .style.setProperty) === */

  /* Colors — primitives */
  --csteer_color_background: #ffffff;
  --csteer_color_foreground: #18181b;
  --csteer_color_muted: #71717a;
  --csteer_color_border: #e4e4e7;

  /* Brand / accent */
  --csteer_color_accent: #2563eb;
  --csteer_color_accent_foreground: #ffffff;
  --csteer_color_accent_hover: #1d4ed8;

  /* Accept button */
  --csteer_accept_background: var(--csteer_color_accent);
  --csteer_accept_foreground: var(--csteer_color_accent_foreground);
  --csteer_accept_background_hover: var(--csteer_color_accent_hover);

  /* Refuse button */
  --csteer_refuse_background: transparent;
  --csteer_refuse_foreground: var(--csteer_color_foreground);
  --csteer_refuse_background_hover: rgba(0, 0, 0, 0.04);

  /* Customize button — same outline style as refuse, distinct token so
   * site owners can color it differently if they want. */
  --csteer_customize_background: transparent;
  --csteer_customize_foreground: var(--csteer_color_foreground);
  --csteer_customize_background_hover: rgba(0, 0, 0, 0.04);

  /* Save (preferences) button — mirrors accept unless overridden. */
  --csteer_save_background: var(--csteer_accept_background);
  --csteer_save_foreground: var(--csteer_accept_foreground);
  --csteer_save_background_hover: var(--csteer_accept_background_hover);

  /* Reopen floating icon */
  --csteer_reopen_wedge_background: #18181b;
  --csteer_reopen_size: 56px;
  --csteer_reopen_offset: 16px;
  --csteer_reopen_top: auto;
  --csteer_reopen_bottom: var(--csteer_reopen_offset);
  --csteer_reopen_left: auto;
  --csteer_reopen_right: var(--csteer_reopen_offset);
  --csteer_reopen_shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
  --csteer_reopen_z_index: 2147483646;

  /* Header / body */
  --csteer_header_foreground: var(--csteer_color_foreground);
  --csteer_header_font_size: 16px;
  --csteer_header_font_weight: 600;
  --csteer_body_foreground: var(--csteer_color_foreground);
  --csteer_body_font_size: 14px;
  --csteer_body_line_height: 1.5;

  /* Link */
  --csteer_link_foreground: var(--csteer_color_accent);
  --csteer_link_foreground_hover: var(--csteer_color_accent_hover);

  /* Layout */
  --csteer_banner_width: 360px;
  --csteer_banner_max_width: calc(100vw - 32px);
  --csteer_banner_padding: 20px;
  --csteer_banner_gap: 12px;
  --csteer_banner_border_radius: 12px;
  --csteer_banner_border_width: 1px;
  /* Pas d'ombre portée sur la bannière (décision 2026-08-31). La variable
     reste déclarée : elle est le seul point d'entrée si l'ombre redevient un
     jour réglable, et elle documente que l'absence est un choix, pas un oubli.
     ⚠️ Aucune règle par format ne doit remettre une valeur en dur ici — c'est
     exactement ce que faisait le popup, court-circuitant la variable. */
  --csteer_banner_shadow: none;

  /* Position (overridden per preset by theme.ts) */
  --csteer_banner_top: auto;
  --csteer_banner_bottom: 16px;
  --csteer_banner_left: auto;
  --csteer_banner_right: 16px;
  --csteer_banner_transform: none;
  --csteer_banner_z_index: 2147483647;

  /* Buttons */
  --csteer_button_padding_x: 16px;
  --csteer_button_padding_y: 8px;
  --csteer_button_border_radius: 8px;
  --csteer_button_font_weight: 500;
  --csteer_button_font_size: 14px;
  --csteer_button_gap: 8px;

  /* Toggle (categories) */
  --csteer_toggle_background: var(--csteer_color_border);
  --csteer_toggle_background_checked: var(--csteer_color_accent);
  --csteer_toggle_thumb: #ffffff;

  /* Typography */
  --csteer_font_family: system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial,
    sans-serif;

  /* Motion */
  --csteer_transition_duration: 200ms;
  --csteer_transition_easing: cubic-bezier(0.16, 1, 0.3, 1);
  /* Travel + scale of the entrance/exit effect. theme.ts derives them from
   * layout.animation and layout.position: "fade" leaves them at rest, "slide"
   * sets the axis matching the format's edge, "zoom" sets the scale. Keeping
   * them as variables means ONE pair of keyframes covers every effect. */
  --csteer_anim_x: 0px;
  --csteer_anim_y: 0px;
  --csteer_anim_scale: 1;
  /* Entrance travel of the floating pill, from its own corner. */
  --csteer_reopen_anim_x: 0px;
  --csteer_reopen_anim_y: 0px;

  font-family: var(--csteer_font_family);
  font-size: var(--csteer_body_font_size);
  line-height: var(--csteer_body_line_height);
  color: var(--csteer_body_foreground);
}

*,
*::before,
*::after {
  box-sizing: border-box;
}

/*
 * RGAA / WCAG — visually-hidden helper, used to add supplementary
 * context for screen readers (e.g. "(opens in a new window)") without
 * cluttering the visual UI.
 */
.csteer_sr_only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/*
 * Respect the user's motion preferences (RGAA 10.13 / WCAG 2.3.3).
 * We don't fully suppress the entrance animation — a tiny duration keeps
 * the layout stable — but we kill all transitions and slow animations.
 */
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.001ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.001ms !important;
  }
}

/* The dialog container receives focus on open (for assistive tech) but must
 * never paint a focus ring — only the buttons show :focus-visible on Tab. */
.csteer_banner:focus {
  outline: none;
}
.csteer_banner {
  position: fixed;
  top: var(--csteer_banner_top);
  bottom: var(--csteer_banner_bottom);
  left: var(--csteer_banner_left);
  right: var(--csteer_banner_right);
  transform: var(--csteer_banner_transform);
  width: var(--csteer_banner_width);
  max-width: var(--csteer_banner_max_width);
  padding: var(--csteer_banner_padding);
  background: var(--csteer_color_background);
  border: var(--csteer_banner_border_width) solid var(--csteer_color_border);
  border-radius: var(--csteer_banner_border_radius);
  box-shadow: var(--csteer_banner_shadow);
  z-index: var(--csteer_banner_z_index);
  display: flex;
  flex-direction: column;
  gap: var(--csteer_banner_gap);
  opacity: 0;
  animation: csteer_banner_in var(--csteer_transition_duration) var(--csteer_transition_easing)
    forwards;
}

/*
 * One pair of keyframes for every effect: the shape of the motion lives in
 * --csteer_anim_{x,y,scale}, which theme.ts sets from layout.animation.
 *
 * Both ends re-state --csteer_banner_transform because that variable also
 * carries the POSITIONING transform (translateX(-50%) to centre a box,
 * translate(-50%,-50%) for the popup). Animating `transform` without it would
 * drop the centring for the length of the animation and make the banner jump.
 * Its rest value is `translate(0)`, never `none` — `transform: none translateY(8px)`
 * does not parse, and the whole declaration is thrown away.
 */
@keyframes csteer_banner_in {
  from {
    opacity: 0;
    transform: var(--csteer_banner_transform, translate(0))
      translate(var(--csteer_anim_x), var(--csteer_anim_y)) scale(var(--csteer_anim_scale));
  }
  to {
    opacity: 1;
    transform: var(--csteer_banner_transform, translate(0)) translate(0, 0) scale(1);
  }
}

@keyframes csteer_banner_out {
  from {
    opacity: 1;
    transform: var(--csteer_banner_transform, translate(0)) translate(0, 0) scale(1);
  }
  to {
    opacity: 0;
    transform: var(--csteer_banner_transform, translate(0))
      translate(var(--csteer_anim_x), var(--csteer_anim_y)) scale(var(--csteer_anim_scale));
  }
}

/* Leaving: banner.ts flips this attribute, waits for animationend (with a
 * timer fallback so a suppressed animation can never strand the element),
 * then removes the host. */
:host([data-anim-state="closing"]) .csteer_banner,
:host[data-anim-state="closing"] .csteer_banner {
  animation-name: csteer_banner_out;
  /* It is on its way out but still painted: it must not swallow clicks aimed
   * at the page underneath, nor at the floating pill fading in behind it. */
  pointer-events: none;
}

/* "none": no motion at all. The base rule starts at opacity 0 and relies on
 * the animation's `forwards` to reveal the banner, so opacity has to be
 * restored here or the banner would never show. */
:host([data-anim="none"]) .csteer_banner,
:host[data-anim="none"] .csteer_banner {
  animation: none;
  opacity: 1;
}

.csteer_header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.csteer_header_left {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.csteer_title {
  margin: 0;
  color: var(--csteer_header_foreground);
  font-size: var(--csteer_header_font_size);
  font-weight: var(--csteer_header_font_weight);
}

.csteer_back_link {
  appearance: none;
  background: transparent;
  border: 0;
  padding: 0;
  margin: 0;
  color: var(--csteer_color_muted);
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  text-align: left;
  align-self: flex-start;
}
.csteer_back_link:hover {
  color: var(--csteer_body_foreground);
  text-decoration: underline;
}

.csteer_close {
  appearance: none;
  background: transparent;
  border: 0;
  color: var(--csteer_color_muted);
  cursor: pointer;
  font-size: 20px;
  line-height: 1;
  /* RGAA / WCAG 2.5.5 — minimum 24×24 CSS px target. */
  min-width: 32px;
  min-height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  flex-shrink: 0;
}
.csteer_close:hover {
  background: rgba(0, 0, 0, 0.04);
  color: var(--csteer_body_foreground);
}

.csteer_body {
  margin: 0;
  color: var(--csteer_body_foreground);
}

.csteer_link {
  color: var(--csteer_link_foreground);
  text-decoration: underline;
}
.csteer_link:hover {
  color: var(--csteer_link_foreground_hover);
}

.csteer_actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--csteer_button_gap);
  margin-top: 4px;
}

.csteer_btn {
  appearance: none;
  border: 1px solid transparent;
  padding: var(--csteer_button_padding_y) var(--csteer_button_padding_x);
  font-family: inherit;
  font-size: var(--csteer_button_font_size);
  font-weight: var(--csteer_button_font_weight);
  border-radius: var(--csteer_button_border_radius);
  cursor: pointer;
  transition: background-color var(--csteer_transition_duration) var(--csteer_transition_easing),
    color var(--csteer_transition_duration) var(--csteer_transition_easing);
}
.csteer_btn:focus-visible {
  outline: 2px solid var(--csteer_color_accent);
  outline-offset: 2px;
}

.csteer_btn_accept {
  background: var(--csteer_accept_background);
  color: var(--csteer_accept_foreground);
  border-color: var(--csteer_accept_background);
  font-size: var(--csteer_accept_font_size, var(--csteer_button_font_size));
  flex: 1;
}
.csteer_btn_accept:hover {
  background: var(--csteer_accept_background_hover);
  border-color: var(--csteer_accept_background_hover);
}

.csteer_btn_refuse {
  background: var(--csteer_refuse_background);
  color: var(--csteer_refuse_foreground);
  border-color: var(--csteer_color_border);
  font-size: var(--csteer_refuse_font_size, var(--csteer_button_font_size));
  flex: 1;
}
.csteer_btn_refuse:hover {
  background: var(--csteer_refuse_background_hover);
}

.csteer_btn_customize {
  background: var(--csteer_customize_background);
  color: var(--csteer_customize_foreground);
  border-color: var(--csteer_color_border);
  font-size: var(--csteer_customize_font_size, var(--csteer_button_font_size));
  flex: 1;
}
.csteer_btn_customize:hover {
  background: var(--csteer_customize_background_hover);
}

.csteer_prefs {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 4px;
  /* Scrollable list when the categories overflow the available banner height. */
  max-height: 320px;
  overflow-y: auto;
  padding-right: 4px;
  /* Subtle scrollbar styling (WebKit / Firefox) */
  scrollbar-width: thin;
  scrollbar-color: var(--csteer_color_border) transparent;
}
.csteer_prefs::-webkit-scrollbar {
  width: 6px;
}
.csteer_prefs::-webkit-scrollbar-thumb {
  background: var(--csteer_color_border);
  border-radius: 999px;
}

.csteer_actions_prefs {
  border-top: 1px solid var(--csteer_color_border);
  padding-top: var(--csteer_banner_gap);
  margin-top: 4px;
}

.csteer_btn_save {
  background: var(--csteer_save_background);
  color: var(--csteer_save_foreground);
  border-color: var(--csteer_save_background);
  font-size: var(--csteer_save_font_size, var(--csteer_button_font_size));
  flex: 1 1 100%;
}
.csteer_btn_save:hover {
  background: var(--csteer_save_background_hover);
  border-color: var(--csteer_save_background_hover);
}

.csteer_pref {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.csteer_pref_label {
  font-size: 13px;
}
.csteer_pref_label small {
  display: block;
  color: var(--csteer_color_muted);
  font-size: 12px;
}

/* Cookie count pill, sits inline with the category label */
.csteer_pref_count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 18px;
  padding: 0 6px;
  margin-left: 6px;
  border-radius: 999px;
  background: var(--csteer_color_border);
  color: var(--csteer_body_foreground);
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  vertical-align: 2px;
}

/* Cookie detail accordion (native <details>) under a category row. */
.csteer_cookies {
  margin: -2px 0 2px;
  font-size: 12px;
}
.csteer_cookies_summary {
  cursor: pointer;
  display: inline-block;
  padding: 2px 0;
  color: var(--csteer_link_foreground);
  font-size: 12px;
  list-style: none;
}
.csteer_cookies_summary::-webkit-details-marker {
  display: none;
}
.csteer_cookies_summary::before {
  content: "▸";
  display: inline-block;
  margin-right: 6px;
  transition: transform 0.15s ease;
}
.csteer_cookies[open] .csteer_cookies_summary::before {
  transform: rotate(90deg);
}
.csteer_cookies_summary:hover {
  text-decoration: underline;
}
.csteer_cookies_list {
  list-style: none;
  margin: 6px 0 4px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.csteer_cookie {
  padding: 8px 10px;
  border: 1px solid var(--csteer_color_border);
  border-radius: 8px;
  background: color-mix(in srgb, var(--csteer_color_border) 18%, transparent);
}
.csteer_cookie_name {
  display: block;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  font-weight: 600;
  color: var(--csteer_body_foreground);
  word-break: break-all;
}
.csteer_cookie_meta {
  display: block;
  margin-top: 1px;
  color: var(--csteer_color_muted);
  font-size: 11px;
}
.csteer_cookie_purpose {
  display: block;
  margin-top: 3px;
  color: var(--csteer_body_foreground);
  font-size: 12px;
  line-height: 1.4;
}

/* Toggle (checkbox visually styled as switch).
 * Sized to satisfy the AA target-size minimum (24×24 CSS px); the
 * extra width gives the switch its idiomatic shape. */
.csteer_toggle {
  position: relative;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
  cursor: pointer;
}
.csteer_toggle input {
  /* Keep the input interactive (focusable, clickable) but visually hidden;
   * the visual track/thumb below mirror its state. We don't use width:0
   * because that breaks focus rings on some browsers. */
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  opacity: 0;
  cursor: pointer;
}
.csteer_toggle .csteer_toggle_track {
  position: absolute;
  inset: 0;
  background: var(--csteer_toggle_background);
  border-radius: 999px;
  transition: background-color var(--csteer_transition_duration) var(--csteer_transition_easing);
  pointer-events: none;
}
.csteer_toggle .csteer_toggle_thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 20px;
  height: 20px;
  background: var(--csteer_toggle_thumb);
  border-radius: 50%;
  transition: transform var(--csteer_transition_duration) var(--csteer_transition_easing);
}
.csteer_toggle input:checked + .csteer_toggle_track {
  background: var(--csteer_toggle_background_checked);
}
.csteer_toggle input:checked + .csteer_toggle_track .csteer_toggle_thumb {
  transform: translateX(20px);
}
.csteer_toggle input:disabled + .csteer_toggle_track {
  opacity: 0.6;
}
.csteer_toggle input:disabled {
  cursor: not-allowed;
}
/* Visible focus ring around the switch when navigating with the keyboard
 * (RGAA 10.7 / WCAG 2.4.7). */
.csteer_toggle input:focus-visible + .csteer_toggle_track {
  outline: 2px solid var(--csteer_color_accent);
  outline-offset: 2px;
}

.csteer_powered {
  margin-top: 4px;
  text-align: right;
  line-height: 0;
}
.csteer_powered img {
  height: 18px;
  width: auto;
  display: inline-block;
  opacity: 0.8;
  transition: opacity var(--csteer_transition_duration) var(--csteer_transition_easing);
}
.csteer_powered a:hover img {
  opacity: 1;
}

/*
 * Format-specific overrides — applied to the shadow host via the
 * `data-format` attribute (set by createBanner() based on layout.position).
 *
 * Sidebar: full-height vertical panel, fixed width, no rounded corners.
 *          Allow the inner banner to grow tall by removing the inline-flex
 *          max-height; the categories list keeps its own scroll.
 *
 * Pushdown: still rendered as position:fixed (visually equivalent to a
 *           banner) but a future enhancement could shift document.body's
 *           top margin so the host content is pushed down for real.
 */
:host([data-format="sidebar"]) .csteer_banner,
:host[data-format="sidebar"] .csteer_banner {
  width: var(--csteer_banner_width, 360px);
  max-width: 100vw;
  border-radius: 0;
  height: 100vh;
  overflow-y: auto;
}

:host([data-format="banner"]) .csteer_banner,
:host[data-format="banner"] .csteer_banner,
:host([data-format="pushdown"]) .csteer_banner,
:host[data-format="pushdown"] .csteer_banner {
  flex-direction: column;
  align-items: stretch;
  border-radius: var(--csteer_banner_border_radius, 0);
  /* Edge-to-edge by default; theme.ts caps --csteer_banner_max_width when
   * layout.maxWidth is set. min() keeps small screens edge-to-edge and the
   * auto margins center the capped strip (left:0 + right:0 + width:100%). */
  max-width: min(var(--csteer_banner_max_width, 100vw), 100vw);
  margin-left: auto;
  margin-right: auto;
}

:host([data-format="popup"]) .csteer_banner,
:host[data-format="popup"] .csteer_banner {
  width: var(--csteer_banner_width, 480px);
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 48px);
  overflow-y: auto;
}
.csteer_powered a {
  color: inherit;
  text-decoration: none;
}

/*
 * Floating reopen icon — rendered in its own shadow host alongside the
 * banner host. The positioning offsets are set by theme.ts based on the
 * configured corner. The host always uses position:fixed so the icon
 * sticks to the viewport.
 *
 * Two formats, switched by the host's [data-reopen-format] attribute:
 *   - "icon"  : transparent background, image displayed alone
 *   - "wedge" : the icon sits on a right-angle wedge filled with
 *               --csteer_reopen_wedge_background, anchored to the
 *               configured corner. The wedge is drawn via clip-path
 *               so the diagonal hypotenuse follows the corner.
 */
.csteer_reopen {
  position: fixed;
  top: var(--csteer_reopen_top);
  bottom: var(--csteer_reopen_bottom);
  left: var(--csteer_reopen_left);
  right: var(--csteer_reopen_right);
  width: var(--csteer_reopen_size);
  height: var(--csteer_reopen_size);
  background: transparent;
  border: 0;
  cursor: pointer;
  z-index: var(--csteer_reopen_z_index);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: transform var(--csteer_transition_duration) var(--csteer_transition_easing);
  animation: csteer_reopen_in var(--csteer_transition_duration) var(--csteer_transition_easing);
}

/*
 * Deliberately NOT `forwards`: the pill keeps a :hover scale, and a filled
 * animation would hold `transform` at its final frame and win over the hover
 * rule for good. Ending unfilled hands `transform` back to the base styles.
 * The base opacity is already 1, so nothing has to be restored.
 */
@keyframes csteer_reopen_in {
  from {
    opacity: 0;
    transform: translate(var(--csteer_reopen_anim_x), var(--csteer_reopen_anim_y));
  }
  to {
    opacity: 1;
    transform: translate(0, 0);
  }
}

:host([data-reopen-anim="none"]) .csteer_reopen,
:host[data-reopen-anim="none"] .csteer_reopen {
  animation: none;
}

.csteer_reopen:hover {
  transform: scale(1.06);
}
.csteer_reopen:focus-visible {
  outline: 2px solid var(--csteer_color_accent);
  outline-offset: 2px;
}

.csteer_reopen_img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  pointer-events: none;
  filter: drop-shadow(var(--csteer_reopen_shadow));
}

/* Wedge format — bigger square host with a triangular background
 * touching the corner. Image stays centered in the inner half. */
:host([data-reopen-format="wedge"]) .csteer_reopen,
:host[data-reopen-format="wedge"] .csteer_reopen {
  width: calc(var(--csteer_reopen_size) * 2);
  height: calc(var(--csteer_reopen_size) * 2);
  /* Override offsets to 0 — the wedge is flush with the corner. */
  top: var(--csteer_reopen_wedge_top, var(--csteer_reopen_top));
  bottom: var(--csteer_reopen_wedge_bottom, var(--csteer_reopen_bottom));
  left: var(--csteer_reopen_wedge_left, var(--csteer_reopen_left));
  right: var(--csteer_reopen_wedge_right, var(--csteer_reopen_right));
}
:host([data-reopen-format="wedge"]) .csteer_reopen::before,
:host[data-reopen-format="wedge"] .csteer_reopen::before {
  content: "";
  position: absolute;
  inset: 0;
  background: var(--csteer_reopen_wedge_background);
  z-index: -1;
  filter: drop-shadow(var(--csteer_reopen_shadow));
}
:host([data-reopen-format="wedge"][data-reopen-position="bottom-right"]) .csteer_reopen::before,
:host[data-reopen-format="wedge"][data-reopen-position="bottom-right"] .csteer_reopen::before {
  clip-path: polygon(100% 0, 100% 100%, 0 100%);
}
:host([data-reopen-format="wedge"][data-reopen-position="bottom-left"]) .csteer_reopen::before,
:host[data-reopen-format="wedge"][data-reopen-position="bottom-left"] .csteer_reopen::before {
  clip-path: polygon(0 0, 100% 100%, 0 100%);
}
:host([data-reopen-format="wedge"][data-reopen-position="top-right"]) .csteer_reopen::before,
:host[data-reopen-format="wedge"][data-reopen-position="top-right"] .csteer_reopen::before {
  clip-path: polygon(0 0, 100% 0, 100% 100%);
}
:host([data-reopen-format="wedge"][data-reopen-position="top-left"]) .csteer_reopen::before,
:host[data-reopen-format="wedge"][data-reopen-position="top-left"] .csteer_reopen::before {
  clip-path: polygon(0 0, 100% 0, 0 100%);
}
:host([data-reopen-format="wedge"]) .csteer_reopen_img,
:host[data-reopen-format="wedge"] .csteer_reopen_img {
  width: 50%;
  height: 50%;
  filter: none;
}
:host([data-reopen-format="wedge"][data-reopen-position="bottom-right"]) .csteer_reopen_img,
:host[data-reopen-format="wedge"][data-reopen-position="bottom-right"] .csteer_reopen_img {
  margin: auto 0 0 auto;
  padding: 0 8% 8% 0;
}
:host([data-reopen-format="wedge"][data-reopen-position="bottom-left"]) .csteer_reopen_img,
:host[data-reopen-format="wedge"][data-reopen-position="bottom-left"] .csteer_reopen_img {
  margin: auto auto 0 0;
  padding: 0 0 8% 8%;
}
:host([data-reopen-format="wedge"][data-reopen-position="top-right"]) .csteer_reopen_img,
:host[data-reopen-format="wedge"][data-reopen-position="top-right"] .csteer_reopen_img {
  margin: 0 0 auto auto;
  padding: 8% 8% 0 0;
}
:host([data-reopen-format="wedge"][data-reopen-position="top-left"]) .csteer_reopen_img,
:host[data-reopen-format="wedge"][data-reopen-position="top-left"] .csteer_reopen_img {
  margin: 0 auto auto 0;
  padding: 8% 0 0 8%;
}
